import React, { useState, useEffect } from "react";
import moment from "moment";
import Parse from "parse";
import useDebounce from "../../hooks";
import { useSelector } from "react-redux";
import { getCurrentUser } from "../../reducers/currentUser";
import { getLists } from "../../reducers/lists";
import HandleDiscountForm from "../../containers/form/HandleDiscountForm";
import HorizontalLinearStepper from "../HorizontalLinearStepper";
import {
  makeStyles,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  AppBar,
  Toolbar,
  IconButton,
} from "@material-ui/core";
import DataGridView from "../reactgrid/DataGridView";
import ClientDiscountQuickEditForm from "../../containers/form/ClientDiscountQuickEditForm";
import { setNumberColumns, filtersQuery, translateColumn } from "../../utils2";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";

const useStyles = makeStyles(() => ({
  tableTopWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "",
  },
  tableTitleWrapper: {
    display: "flex",
    alignItems: "center",
    minHeight: "64px",
    padding: "0 8px 0 24px",
  },
  addBtnWrapper: {
    display: "flex",
    alignItems: "center",
    padding: "0 24px",
  },
  toolbar: {
    backgroundColor: "#46494c", //'#02cfa3'
  },
  noDiscount: {
    textAlign: "center",
    margin: "10rem 0 0",
  },
  expiré: {
    background:
      "repeating-linear-gradient(135deg, #fff0f0, #fff0f0 8px, #fff6f6 8px, #fff6f6 16px)",
    color: "#fff",
  },
}));

const asyncForEach = async (array, callback) => {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array);
  }
};

const thisYear = parseInt(moment().format("YYYY"));
const lastYear = parseInt(thisYear) - 1;
const columns = [
  { name: "includedClients", title: "Magasin(s) concerné(s)" },
  { name: "turnOverLastYear", title: "CA " + lastYear, type: "numeric" },
  { name: "turnOverThisYear", title: "CA " + thisYear, type: "numeric" },
  { name: "startDate", title: "Début", type: "date" },
  { name: "endDate", title: "Fin", type: "date" },
  { name: "percentageDiscount", title: "Remise", type: "numeric" },
  { name: "francoMinAmount", title: "Franco", type: "numeric" },
  { name: "groupPercentageDiscount", title: "Rem grp.", type: "string" },
  {
    name: "endOfYearGroupPercentageDiscount",
    title: "RFA grp.",
    type: "string",
  },
  { name: "tradeNamePercentageDiscount", title: "Rem ens.", type: "string" },
  {
    name: "endOfYearTradeNamePercentageDiscount",
    title: "RFA ens.",
    type: "string",
  },
  { name: "group", title: "Groupement", type: "string" },
  { name: "tradeName", title: "Enseigne", type: "string" },
  { name: "updatedAt", title: "Dernière modif.", type: "date" },
  { name: "lastUpdater", title: "Modifié par", type: "string" },
];
const columnExtensions = [
  { columnName: "includedClients", width: 170, wordWrapEnabled: true },
  {
    columnName: "turnOverLastYear",
    width: 75,
    align: "right",
    filteringEnabled: false,
  },
  {
    columnName: "turnOverThisYear",
    width: 75,
    align: "right",
    filteringEnabled: false,
  },
  { columnName: "startDate", width: 125, align: "right" },
  { columnName: "endDate", width: 125, align: "right" },
  { columnName: "percentageDiscount", width: 70, align: "right" },
  { columnName: "francoMinAmount", width: 70, align: "right" },
  {
    columnName: "groupPercentageDiscount",
    width: 70,
    align: "right",
    filteringEnabled: false,
  },
  {
    columnName: "endOfYearGroupPercentageDiscount",
    width: 70,
    align: "right",
    filteringEnabled: false,
  },
  {
    columnName: "tradeNamePercentageDiscount",
    width: 70,
    align: "right",
    filteringEnabled: false,
  },
  {
    columnName: "endOfYearTradeNamePercentageDiscount",
    width: 70,
    align: "right",
    filteringEnabled: false,
  },
  { columnName: "group", width: 200, wordWrapEnabled: true },
  { columnName: "tradeName", width: 200, wordWrapEnabled: true },
  { columnName: "updatedAt" },
  { columnName: "lastUpdater", width: 70 },
];
const pageSizes = [15, 25, 50, 100, 150];

const ClientDiscountList = (props) => {
  const { editable, readOnly, addable, clientCode } = props;
  const classes = useStyles();
  const currentUser = useSelector(getCurrentUser);
  const userRole =
    currentUser !== undefined ? currentUser.get("role") : undefined;
  const listsOptions = useSelector(getLists);
  const groupsOptions = listsOptions.find(
    (listOptions) => listOptions.listName === "groups"
  ).options;
  const tradeNamesOptions = listsOptions.find(
    (listOptions) => listOptions.listName === "tradenames"
  ).options;
  const [numberColumns, setStateNumberColumns] = useState([]);
  const [filters, setFilters] = useState([]);
  const debouncedFilters = useDebounce(filters, 500);
  const [sorting, setSorting] = useState([
    { columnName: "includedClients.CLKTCODE", direction: "asc" },
  ]);
  const [totalCount, setTotalCount] = useState(100);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [discounts, setDiscounts] = useState(undefined);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarText, setSnackBarText] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [quickEditDialogOpen, setQuickEditDialogOpen] = useState(false);
  const [quickEditDiscountId, setQuickEditDiscountId] = useState(false);
  const [dialogTitle, setDialogTitle] = useState(null);
  const [dialogText, setDialogText] = useState(null);
  const [dialogYesBtnText, setDialogYesBtnText] = useState(null);
  const [dialogNoBtnText, setDialogNoBtnText] = useState(null);
  const [dialogOperation, setDialogOperation] = useState(null);
  const [discountId, setDiscountId] = useState(null);
  const [columnsTranslations, setColumnsTranslations] = useState([]);

  /********************************************************/
  /************ content info for paging panel**************/
  /********************************************************/
  const contentInfo = ({ from, to, count }) => {
    return `${from} ${from < to ? ` à ${to}` : ""} sur ${count} remise(s)`;
  };

  /********************************************************/
  /*********************** Navigation *********************/
  /********************************************************/
  const changeFilters = (filters) => {
    setLoading(true);
    setFilters(filters);
  };
  const changeSorting = (sorting) => {
    setLoading(true);
    setSorting(sorting);
  };
  const changeCurrentPage = (currentPage) => {
    setLoading(true);
    setCurrentPage(currentPage);
  };
  const changePageSize = (pageSize) => setPageSize(pageSize);

  /********************************************************/
  /*********** Table data and clicl func ******************/
  /********************************************************/
  const getDiscounts = async () => {
    if (columnsTranslations.length < 1) {
      return null;
    }

    let queryResult;

    const query = new Parse.Query("Discounts")
      .select(
        "includedClients",
        "startDate",
        "endDate",
        "percentageDiscount",
        "francoMinAmount",
        "createdAt",
        "creator",
        "updatedAt",
        "lastUpdater",
        "status"
      )
      .include("includedClients", "creator", "lastUpdater")
      .equalTo("type", "clientPermanent");

    if (userRole === "commercial") {
      // commercial can change his clients settings
      const commercialClients = await new Parse.Query("Clients")
        .equalTo("CLCTREP1", currentUser.get("commercial"))
        .limit(1000000)
        .find();
      if (commercialClients) {
        query.containedIn("includedClients", commercialClients);
      }
    }

    // code for client section
    if (clientCode !== undefined) {
      const client = await new Parse.Query("Clients")
        .equalTo("CLKTCODE", clientCode)
        .first();
      query.equalTo("includedClients", client);
    }

    // 'percentageDiscount' & 'francoMinAmount' are stored as strings... should have been numerics
    const arrayOfColumnsToIgnore = [
      "includedClients",
      "group",
      "tradeName",
      "percentageDiscount",
      "francoMinAmount",
    ];
    filtersQuery(
      query,
      columnsTranslations,
      { columns, filters, sorting },
      arrayOfColumnsToIgnore
    );

    // filtering special columns
    const includedClientsFilter = filters.find(
      (filter) => filter.columnName === "includedClients"
    );
    const groupFilter = filters.find((filter) => filter.columnName === "group");
    const groupOptionsFiltered =
      groupFilter !== undefined
        ? groupsOptions.filter((option) =>
            option.text.toLowerCase().includes(groupFilter.value.toLowerCase())
          )
        : [];
    const groupIdsFiltered = groupOptionsFiltered.map((option) => option.value);
    const tradeNameFilter = filters.find(
      (filter) => filter.columnName === "tradeName"
    );
    const tradeNameOptionsFiltered =
      tradeNameFilter !== undefined
        ? tradeNamesOptions.filter((option) =>
            option.text
              .toLowerCase()
              .includes(tradeNameFilter.value.toLowerCase())
          )
        : [];
    const tradeNameIdsFiltered = tradeNameOptionsFiltered.map(
      (option) => option.value
    );
    if (
      includedClientsFilter !== undefined ||
      groupFilter !== undefined ||
      tradeNameFilter !== undefined
    ) {
      const includedClients = new Parse.Query("Clients");

      if (includedClientsFilter !== undefined) {
        includedClients.matches("CLCTNOM", includedClientsFilter.value, "i");
      }
      if (groupIdsFiltered.length) {
        includedClients.containedIn("CLCTCENACH", groupIdsFiltered);
      }
      if (tradeNameIdsFiltered.length) {
        console.log("groupIdsFiltered: ", tradeNameIdsFiltered);
        includedClients.containedIn("CLCTGROUPE", tradeNameIdsFiltered);
      }
      // const includedClients = await incClients.find();
      query.matchesQuery("includedClients", includedClients);
    }

    const percentageDiscountFilter = filters.find(
      (filter) => filter.columnName === "percentageDiscount"
    );
    if (percentageDiscountFilter !== undefined) {
      query.equalTo("percentageDiscount", percentageDiscountFilter.value);
    }

    const francoMinAmountFilter = filters.find(
      (filter) => filter.columnName === "francoMinAmount"
    );
    if (francoMinAmountFilter !== undefined) {
      query.equalTo("francoMinAmount", francoMinAmountFilter.value);
    }
    // end filtering special columns

    const totalCount = await query.count();
    // skipping according to currentPage and pageSize
    queryResult = await query
      .addAscending("name")
      .skip(pageSize * currentPage)
      .limit(pageSize)
      .find();

    const groups = await new Parse.Query("Groups")
      .select("CLKTCODE", "RFAPercentage")
      .find();
    const groupDiscounts = await new Parse.Query("Discounts")
      .select(
        "includedGroups",
        "startDate",
        "endDate",
        "percentageDiscount",
        "status"
      )
      .include("includedGroups")
      .equalTo("type", "clientPermanent")
      .exists("includedGroups")
      .find();
    const tradeNames = await new Parse.Query("Tradenames")
      .select("CLCTGROUPE", "RFAPercentage")
      .find();
    const tradeNameDiscounts = await new Parse.Query("Discounts")
      .select(
        "includedTradeNames",
        "startDate",
        "endDate",
        "percentageDiscount",
        "status"
      )
      .include("includedTradeNames")
      .equalTo("type", "clientPermanent")
      .exists("includedTradeNames")
      .find();

    const discounts = [];

    // formatting data
    if (Array.isArray(queryResult)) {
      // Get all unique client IDs from discounts
      const clientIds = [
        ...new Set(
          queryResult
            .map((discount) =>
              discount
                .get("includedClients")
                ?.map((client) => client.get("CLKTCODE"))
            )
            .filter(Boolean)
        ),
      ];

      // Query all StatsForClients in a single request
      const clientStats = await new Parse.Query("StatsForClients")
        .select("clientId", "turnover_" + lastYear, "turnover_" + thisYear)
        .containedIn("clientId", clientIds)
        .find();

      // Create a map of clientId to stats
      const clientStatsMap = clientStats.reduce((acc, stat) => {
        acc[stat.get("clientId")] = {
          lastYear: stat.get("turnover_" + lastYear),
          thisYear: stat.get("turnover_" + thisYear),
        };
        return acc;
      }, {});

      await asyncForEach(queryResult, async (discount) => {
        let data = { ...discount.attributes }; // shallow copy to complete object
        if (!discount.get("includedClients")) return;
        data.id = discount.id;

        // from array of pointers to array of strings
        const includedClients = discount
          .get("includedClients")
          .map((includedClient) => includedClient.get("CLKTCODE"));
        data.includedClients = translateColumn(
          includedClients,
          columnsTranslations.find(
            (columnTranslation) =>
              columnTranslation.columnName === "includedClients"
          )
        );

        // for turnover we only take the turnover of first client (99.9% of the time there is only one)
        const stats = clientStatsMap[includedClients[0]] || {};
        data.turnOverLastYear =
          stats.lastYear !== undefined ? Math.round(stats.lastYear) : 0;
        data.turnOverThisYear =
          stats.thisYear !== undefined ? Math.round(stats.thisYear) : 0;

        const groupId = discount.get("includedClients")[0].get("CLCTCENACH");
        data.group =
          groupId !== undefined && groupId[0] === "9"
            ? translateColumn(
                groupId,
                columnsTranslations.find(
                  (columnTranslation) =>
                    columnTranslation.columnName === "group"
                )
              )
            : "";
        const tradeNameId = discount
          .get("includedClients")[0]
          .get("CLCTGROUPE");
        data.tradeName =
          tradeNameId !== undefined
            ? translateColumn(
                tradeNameId,
                columnsTranslations.find(
                  (columnTranslation) =>
                    columnTranslation.columnName === "tradeName"
                )
              )
            : "";

        // formatting dates
        data.updatedAt = moment(data.updatedAt).format("DD/MM/YYYY HH:mm");
        data.createdAt = moment(data.createdAt).format("DD/MM/YYYY HH:mm");
        data.startDate = moment(data.startDate).format("DD/MM/YYYY");
        data.endDate = moment(data.endDate).format("DD/MM/YYYY");

        // formatting lastUpdater name
        data.lastUpdater =
          data.lastUpdater.get("firstname").charAt(0) +
          ". " +
          data.lastUpdater.get("lastname");

        data.groupPercentageDiscount = "-";
        groupDiscounts.forEach((groupDiscount) => {
          const includedGroup = groupDiscount.get("includedGroups")[0]; // we assume that there's only one includedGroup
          const includedClient = discount.get("includedClients")[0]; // we assume that there's only one includedClient
          includedGroup.get("clients").forEach((groupClient) => {
            if (
              groupClient.id === includedClient.id &&
              moment().isBetween(
                groupDiscount.get("startDate"),
                groupDiscount.get("endDate")
              )
            ) {
              data.groupPercentageDiscount =
                groupDiscount.get("percentageDiscount");
            }
          });
        });

        data.endOfYearGroupPercentageDiscount = "-";
        groups.forEach((group) => {
          if (
            groupId === group.get("CLKTCODE") &&
            group.get("RFAPercentage") !== undefined
          ) {
            data.endOfYearGroupPercentageDiscount = group.get("RFAPercentage");
          }
        });

        data.tradeNamePercentageDiscount = "-";
        tradeNameDiscounts.forEach((tradeNameDiscount) => {
          const includedTradeName =
            tradeNameDiscount.get("includedTradeNames")[0]; // we assume that there's only one includedGroup
          const includedClient = discount.get("includedClients")[0]; // we assume that there's only one includedClient
          includedTradeName.get("clients").forEach((tradeNameClient) => {
            if (
              tradeNameClient.id === includedClient.id &&
              moment().isBetween(
                tradeNameDiscount.get("startDate"),
                tradeNameDiscount.get("endDate")
              )
            ) {
              data.tradeNamePercentageDiscount =
                tradeNameDiscount.get("percentageDiscount");
            }
          });
        });
        // to be done CLCTGROUPE
        data.endOfYearTradeNamePercentageDiscount = "-";
        tradeNames.forEach((tradeName) => {
          if (
            tradeNameId === tradeName.get("CLCTGROUPE") &&
            tradeName.get("RFAPercentage") !== undefined
          ) {
            data.endOfYearTradeNamePercentageDiscount =
              tradeName.get("RFAPercentage");
          }
        });

        discounts.push(data);
      });
    }
    setDiscounts(discounts);
    setTotalCount(totalCount);
    setLoading(false);
  };

  const listToColumnTranslations = (arrayOfListNameAndColumnName) => {
    const newColumnTranslations = [];
    arrayOfListNameAndColumnName.forEach((listNameAndColumnName) => {
      let listOfOptions = [];
      // getting list
      listsOptions.forEach((list) => {
        if (list.listName === listNameAndColumnName.listName) {
          listOfOptions = list.options;
        }
      });
      newColumnTranslations.push({
        columnName: listNameAndColumnName.columnName,
        values: listOfOptions.map((option) => [option.value, option.text]),
      });
    });
    setColumnsTranslations([...columnsTranslations, ...newColumnTranslations]);
  };
  // when dialog is called
  const handleClickOpenByCell = (props) => {
    if (editable) {
      setQuickEditDiscountId(props.row.id);
      setDialogTitle("Remise permanente " + props.row.includedClients);
      setQuickEditDialogOpen(true);
    }
  };

  /********************************************************/
  /************************* Modal ************************/
  /********************************************************/
  const handleClickOpen = (data, operation) => {
    switch (operation) {
      case "add":
        setDialogTitle("Création d'une remise");
        setDialogText("");
        setDialogYesBtnText("ajouter");
        setDialogNoBtnText("annuler");
        break;
      case "modify":
        setDialogTitle("Modification d'une remise");
        setDialogText("");
        setDialogYesBtnText("modifier");
        setDialogNoBtnText("annuler");
        setDiscountId(data);
        break;
      case "delete":
        setDialogTitle("Suppression d'une remise");
        setDialogText(
          "La remise va être supprimée. Souhaitez-vous continuer ?"
        );
        setDialogYesBtnText("oui");
        setDialogNoBtnText("non");
        setDiscountId(data.row.id);
        break;
      default:
    }
    setDialogOpen(true);
    setDialogOperation(operation);
  };

  // when closing dialog box
  const handleModalClose = async (action) => {
    switch (action) {
      case "discountAdded":
        handleSnackBarShow("remise ajoutée");
        break;
      case "discountUpdated":
        handleSnackBarShow("remise mise à jour");
        break;
      case "discountDeleted":
        handleSnackBarShow("remise supprimée");
        break;
      default:
    }
    setDialogOpen(false);
    setQuickEditDialogOpen(false);
  };

  const handleModalValidate = async (discountData, operation) => {
    switch (operation) {
      case "delete":
        const discount = await new Parse.Query("Discounts").get(discountId);
        if (discount) {
          try {
            await discount.destroy();
          } catch (error) {
            console.log("Error: " + error.code + " " + error.message);
          }
        }
        await handleModalClose("discountDeleted");
        break;
      default:
        // for add and modify
        document
          .getElementById("editForm")
          .dispatchEvent(new Event("submit", { cancelable: true }));
        break;
    }
  };

  /********************************************************/
  /************************ actions ***********************/
  /********************************************************/
  const actions = [
    {
      icon: <EditIcon />,
      action: (rowData) => handleClickOpen(rowData.row.id, "modify"),
    },
    {
      icon: <DeleteIcon />,
      action: (rowData) => handleClickOpen(rowData, "delete"),
    },
  ];

  /********************************************************/
  /************************ SnackBar **********************/
  /********************************************************/
  const handleSnackBarShow = (textValue) => {
    setSnackBarOpen(true);
    setSnackBarText(textValue);
  };
  const handleSnackBarClose = () => {
    setSnackBarOpen(false);
  };

  /********************************************************/
  /************************ Stepper ***********************/
  /********************************************************/
  const getSteps = () => {
    return ["Nom et type", "Périmètre", "Période", "Conditions et remise"];
  };
  const getOptionalSteps = () => {
    return [];
  };
  const getStepContent = (activeStep, handleBack, handleNext, handleSkip) => {
    return (
      <HandleDiscountForm
        steps={getSteps()}
        activeStep={activeStep}
        optionalSteps={getOptionalSteps()}
        operation={dialogOperation}
        discountId={discountId}
        onHandleModalClose={handleModalClose}
        onChosenDiscountType={(dialogTitle) => setDialogTitle(dialogTitle)}
        onHandleBack={handleBack}
        onHandleNext={handleNext}
        onHandleSkip={handleSkip}
        currentUser={currentUser}
      />
    );
  };

  useEffect(() => {
    listToColumnTranslations([
      { listName: "clients", columnName: "includedClients" },
      { listName: "groups", columnName: "group" },
      { listName: "tradenames", columnName: "tradeName" },
    ]);
    setNumberColumns(columns, setStateNumberColumns);
    // eslint-disable-next-line
  }, []);

  // when page or sorting change or dialog is closed
  useEffect(() => {
    getDiscounts();
    // eslint-disable-next-line
  }, [
    currentPage,
    sorting,
    pageSize,
    dialogOpen,
    quickEditDialogOpen,
    columnsTranslations,
  ]);

  // when filters change
  useEffect(() => {
    if (debouncedFilters) {
      getDiscounts();
    }
    // eslint-disable-next-line
  }, [debouncedFilters]);

  if (discounts === undefined) {
    return null;
  }

  const tableHasData = discounts !== undefined && discounts.length > 0;
  return (
    <>
      {!tableHasData && readOnly && (
        <Typography variant="body1" className={classes.noDiscount}>
          Aucune remise n'a été trouvée
        </Typography>
      )}
      {/* if table is empty we only display it in Discount section because it's the only way to add discounts! */}
      {(tableHasData || !readOnly) && (
        <Paper>
          <DataGridView
            title="Remises Client Permanentes"
            withAddButton={addable}
            onAddButtonClick={() => handleClickOpen(null, "add")}
            rowsValues={discounts}
            columnHeaders={columns}
            currentPage={currentPage}
            onCurrentPageChange={changeCurrentPage}
            pageSize={pageSize}
            pageSizes={pageSizes}
            onPageSizeChange={changePageSize}
            filters={filters}
            onFiltersChange={changeFilters}
            sorting={sorting}
            onSortingChange={changeSorting}
            totalCount={totalCount}
            loading={loading}
            numberColumns={numberColumns}
            rowsPerPage="Nb de remises par page:"
            contentInfoSupplier={contentInfo}
            columnExtensions={columnExtensions}
            withPagination={true}
            hasActions={tableHasData && !readOnly}
            actions={actions}
            noDataText="pas de remise client permanentes"
            withOnCellClickFunction
            onCellClickFunction={handleClickOpenByCell}
          />
        </Paper>
      )}

      <Dialog
        fullScreen={dialogOperation !== "delete"}
        open={dialogOpen}
        onClose={handleModalClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        {dialogOperation !== "delete" && (
          <AppBar>
            <Toolbar className={classes.toolbar}>
              <IconButton
                color="inherit"
                onClick={handleModalClose}
                aria-label="Close"
              >
                <CloseIcon />
              </IconButton>
              <Typography variant="h6" color="inherit" className={classes.flex}>
                {dialogTitle}
              </Typography>
            </Toolbar>
          </AppBar>
        )}

        {dialogOperation === "delete" && (
          <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
        )}

        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogText}
          </DialogContentText>
          {(dialogOperation === "add" || dialogOperation === "modify") && (
            <HorizontalLinearStepper
              onGetSteps={getSteps()}
              onGetStepContent={getStepContent}
              optionalSteps={getOptionalSteps()}
              navigationIsExternal
              dialogOperation={dialogOperation}
              onHandleModalValidate={handleModalValidate}
              onHandleModalClose={handleModalClose}
            />
          )}
        </DialogContent>

        {dialogOperation === "delete" && (
          <DialogActions>
            <Button
              onClick={() => handleModalValidate("", "delete")}
              color="primary"
            >
              {dialogYesBtnText}
            </Button>
            <Button
              onClick={() => handleModalClose()}
              color="primary"
              autoFocus
            >
              {dialogNoBtnText}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* for quick edit */}
      <Dialog
        open={quickEditDialogOpen}
        onClose={handleModalClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xs"
      >
        <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
        <DialogContent>
          <ClientDiscountQuickEditForm
            id={quickEditDiscountId}
            onDiscountUpdate={() => handleModalClose("discountUpdated")}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={3000}
        open={snackBarOpen}
        onClose={handleSnackBarClose}
        ContentProps={{
          "aria-describedby": "message-id",
        }}
        message={<span id="message-id">{snackBarText}</span>}
      />
    </>
  );
};

export default ClientDiscountList;
